/*
 *** Usage in FO: OK
 *** Usage in PO: OK
 *** Usage in BO: NOT OK
 */

import PropTypes from 'prop-types';
import { createElement, useState, useCallback, useRef, useEffect } from 'react';
import styled, { css } from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { Button, ButtonClose } from '@hivebrite/design-system/Button';
import { Loader } from '@hivebrite/design-system/Loader';

import onEnterPress from 'hb-react/shared/utils/onEnterPress';

/**
 * @deprecated please use `@hivebrite/design-system/SingleImageUpload` instead
 */
const UploadImage = ({
  acceptFiles,
  children,
  dataTestId,
  icon,
  initialPreview,
  name,
  onChange,
  width,
  height,
  renderEditButton,
  isLoading,
  isDefaultPicture,
}) => {
  const inputFileRef = useRef(null);

  const [previewImage, setPreviewImage] = useState(initialPreview);
  const [isDefaultImage, setIsDefaultImage] = useState(isDefaultPicture);

  const handleClick = useCallback(() => {
    inputFileRef.current.value = null;
    inputFileRef.current.click();
  }, []);

  const handleChangeInputFile = useCallback((fileOpenEvent) => {
    const [file] = fileOpenEvent.target.files;

    if (!acceptFiles.includes(file.type)) {
      return;
    }

    const reader = new FileReader();

    reader.onload = (fileReadEvent) => {
      setPreviewImage(fileReadEvent.target.result);
      if (onChange) {
        onChange(fileOpenEvent, fileReadEvent);
      }
    };

    reader.readAsDataURL(file);
  }, []);

  const handleResetPreviewImage = useCallback(() => {
    inputFileRef.current.value = null;

    setPreviewImage(null);
    onChange(null);
  }, []);

  useEffect(() => {
    setPreviewImage(initialPreview);
  }, [initialPreview]);

  useEffect(() => {
    setIsDefaultImage(isDefaultPicture);
  }, [isDefaultPicture]);

  const canInteractWith = !previewImage
    ? handleClick
    : isDefaultImage
    ? handleClick
    : undefined;

  const wrapperUploadImageTabIndex = !previewImage || isDefaultImage ? 0 : -1; // to avoid nested ternary

  return (
    <Box flexDirection="column" data-testid={dataTestId}>
      {renderEditButton ? (
        <Box pb={3}>
          <Button onClick={handleClick}>{__('Edit Image')}</Button>
        </Box>
      ) : null}

      <WrapperUploadImage
        onClick={canInteractWith}
        onKeyPress={onEnterPress(canInteractWith)}
        tabIndex={wrapperUploadImageTabIndex}
        width={width}
        height={height}
      >
        {isLoading ? (
          <Loader size={30} />
        ) : previewImage ? (
          <>
            {isDefaultImage ? null : (
              <ClearButtonWrapper>
                <ButtonClose
                  variant="inversed"
                  onClick={handleResetPreviewImage}
                  aria-label={__('Remove image')}
                />
              </ClearButtonWrapper>
            )}
            <img src={previewImage} alt="" />
          </>
        ) : (
          <>
            {icon && (
              <Box mb={children && 4} color="primary" height={80} width={80}>
                {createElement(icon, {
                  height: '100%',
                  width: '100%',
                  // @hivebrite/design-system icons use `size` props instead of the `width` and `height` ones.
                  size: '100%',
                })}
              </Box>
            )}
            {children}
          </>
        )}
        <input
          aria-hidden
          hidden
          tabIndex={-1}
          ref={inputFileRef}
          accept={acceptFiles.join(',')}
          name={name}
          onChange={handleChangeInputFile}
          type="file"
          // eslint-disable-next-line react/no-unknown-property
          css={css`
            position: absolute;
            height: 0;
            opacity: 0;
            width: 0;
          `}
        />
      </WrapperUploadImage>
    </Box>
  );
};

UploadImage.propTypes = {
  acceptFiles: PropTypes.array,
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.object]).isRequired,
  isDefaultPicture: PropTypes.bool,
  isLoading: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  renderEditButton: PropTypes.bool,
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.object]).isRequired,
};

UploadImage.defaultProps = {
  acceptFiles: ['image/gif', 'image/png', 'image/jpeg'],
  isDefaultPicture: false,
  isLoading: false,
  renderEditButton: false,
};

export default UploadImage;

const WrapperUploadImage = styled(Box).attrs(({ width, height }) => ({
  alignItems: 'center',
  bg: 'white',
  flexDirection: 'column',
  height,
  justifyContent: 'center',
  mb: 6,
  mr: { _: 0, m: 4 },
  width,
}))`
  border-radius: 4px;
  border: 1px solid ${(props) => props.theme.colors.dove};
  cursor: ${(props) => props.onClick && 'pointer'};
  overflow: hidden;
  position: relative;
  user-select: none;

  img {
    bottom: 0;
    left: 0;
    position: absolute;
    right: 0;
    top: 0;
    width: inherit;
    height: 100%;
    object-fit: cover;
  }
`;
const ClearButtonWrapper = styled(Box).attrs(() => ({}))`
  position: absolute;
  top: ${(props) => props.theme.space[2]}px;
  right: ${(props) => props.theme.space[2]}px;
  z-index: 1;
`;
